export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

type Query = Record<string, string | number | boolean | null | undefined | string[]>;

function buildUrl(path: string, query?: Query): string {
  const url = new URL(`/api/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((item) => url.searchParams.append(k, item));
    else url.searchParams.set(k, String(v));
  }
  return url.pathname + url.search;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const isForm = body instanceof FormData;
  const res = await fetch(buildUrl(path, query), {
    method,
    credentials: "same-origin",
    headers: {
      "X-Requested-With": "savingapp",
      ...(body !== undefined && !isForm ? { "Content-Type": "application/json" } : {}),
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });
  if (res.status === 401 && !path.startsWith("/auth/")) onUnauthorized?.();
  if (!res.ok) {
    let message = "Algo salió mal. Intenta de nuevo.";
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string") message = data.detail;
      else if (Array.isArray(data.detail)) message = "Revisa los datos del formulario.";
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>("GET", path, undefined, query),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  delete: <T = void>(path: string, body?: unknown) => request<T>("DELETE", path, body),
  upload: <T>(path: string, form: FormData) => request<T>("POST", path, form),
  downloadBlob: async (path: string, query?: Query): Promise<Blob> => {
    const res = await fetch(buildUrl(path, query), {
      method: "GET",
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "savingapp",
      },
    });
    if (res.status === 401 && !path.startsWith("/auth/")) onUnauthorized?.();
    if (!res.ok) {
      let message = "Algo salió mal. Intenta de nuevo.";
      try {
        const data = (await res.json()) as { detail?: unknown };
        if (typeof data.detail === "string") message = data.detail;
      } catch {
        /* non-JSON */
      }
      throw new ApiError(res.status, message);
    }
    return await res.blob();
  },
};

export async function downloadHouseholdZip(): Promise<void> {
  const blob = await api.downloadBlob("/household/export");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "datos-hogar.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
